// Making a discount offer (ADR 0100): percent off (with an optional "up to" amount),
// rupees off, a fixed price, or words only; on every dish, chosen dishes, or a category.
//
// The server decides what is allowed; this form says the same things in words before
// anything is sent. Percentages are typed as percentages and sent as basis points,
// rupees as paise.

import { useMemo, useState, type FormEvent, type JSX } from 'react';
import { ApiError } from '../../api/client.js';
import type { CreateDiscountOfferBody, DiscountType, MenuDetail } from '../../api/types.js';

const DEFAULT_LANG = 'en';

type Cover = 'all' | 'items' | 'category';

export interface DiscountFormProps {
  readonly menu: MenuDetail;
  readonly create: {
    readonly mutate: (body: CreateDiscountOfferBody, opts?: { onSuccess?: () => void }) => void;
    readonly isPending: boolean;
    readonly error: Error | null;
  };
}

const KINDS: readonly { readonly value: DiscountType; readonly label: string }[] = [
  { value: 'percent', label: 'Percent off' },
  { value: 'flat', label: 'Rupees off' },
  { value: 'fixed_price', label: 'Fixed price' },
  { value: 'text', label: 'Words only' },
];

const name = (t: readonly { languageCode: string; name: string }[]): string =>
  (t.find((x) => x.languageCode === DEFAULT_LANG) ?? t[0])?.name ?? '(untitled)';

export function DiscountOfferForm({ menu, create }: DiscountFormProps): JSX.Element {
  const dishes = useMemo(
    () =>
      menu.categories.flatMap((c) =>
        c.items.map((i) => ({
          id: i.id,
          label: `${name(i.translations)} — ${name(c.translations)}`,
        })),
      ),
    [menu],
  );
  const categories = useMemo(
    () => menu.categories.map((c) => ({ id: c.id, label: name(c.translations) })),
    [menu],
  );

  const [kind, setKind] = useState<DiscountType>('percent');
  const [amount, setAmount] = useState('');
  const [cap, setCap] = useState('');
  const [label, setLabel] = useState('');
  const [cover, setCover] = useState<Cover>('all');
  const [chosen, setChosen] = useState<readonly string[]>([]);
  const [categoryId, setCategoryId] = useState(categories[0]?.id ?? '');
  const [from, setFrom] = useState(defaultFrom());
  const [to, setTo] = useState(defaultTo());
  const [problem, setProblem] = useState<string | null>(null);

  // A fixed price is for one dish.
  const effectiveCover: Cover = kind === 'fixed_price' ? 'items' : cover;

  function toggle(id: string): void {
    setChosen(chosen.includes(id) ? chosen.filter((x) => x !== id) : [...chosen, id]);
    setProblem(null);
  }

  function reset(): void {
    setAmount('');
    setCap('');
    setLabel('');
    setChosen([]);
    setProblem(null);
  }

  function onSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const a = fromLocal(from);
    const b = fromLocal(to);
    if (!a || !b) return setProblem('Pick valid dates.');
    if (Date.parse(b) <= Date.parse(a)) return setProblem('The end must be after the start.');

    const num = Number(amount);
    let value: number | undefined;
    let capPaise: number | undefined;
    if (kind === 'percent') {
      if (amount.trim() === '' || !Number.isFinite(num) || num < 0.01 || num > 99) {
        return setProblem('Type a percentage from 1 to 99.');
      }
      value = Math.round(num * 100);
      if (cap.trim() !== '') {
        const c = Number(cap);
        if (!Number.isFinite(c) || c < 0.01)
          return setProblem('“Up to” must be an amount in rupees.');
        capPaise = Math.round(c * 100);
      }
    } else if (kind === 'flat' || kind === 'fixed_price') {
      if (amount.trim() === '' || !Number.isFinite(num) || num < 0.01) {
        return setProblem('Type an amount in rupees.');
      }
      value = Math.round(num * 100);
    } else if (label.trim() === '') {
      return setProblem('Type the words to show on the dish.');
    }

    let target: CreateDiscountOfferBody['target'];
    if (effectiveCover === 'all') target = { kind: 'all' };
    else if (effectiveCover === 'items') {
      if (chosen.length === 0) return setProblem('Choose at least one dish.');
      if (kind === 'fixed_price' && chosen.length !== 1) {
        return setProblem('A fixed price is for one dish. Choose just one.');
      }
      target = { kind: 'items', itemIds: chosen };
    } else {
      if (!categoryId) return setProblem('Choose a category.');
      target = { kind: 'category', categoryId };
    }

    setProblem(null);
    create.mutate(
      {
        scope: 'discount',
        activatesAt: a,
        expiresAt: b,
        discountType: kind,
        ...(value !== undefined ? { value } : {}),
        ...(capPaise !== undefined ? { capPaise } : {}),
        ...(label.trim() ? { label: label.trim() } : {}),
        target,
      },
      { onSuccess: reset },
    );
  }

  return (
    <form onSubmit={onSubmit} className="offer-form" aria-label="new offer">
      <fieldset>
        <legend className="muted">What kind of offer</legend>
        {KINDS.map((k) => (
          <label className="radio" key={k.value}>
            <input
              type="radio"
              name="discount-kind"
              checked={kind === k.value}
              onChange={() => {
                setKind(k.value);
                setProblem(null);
              }}
            />
            {k.label}
          </label>
        ))}
      </fieldset>

      {kind === 'percent' ? (
        <div className="row">
          <div style={{ flex: 1 }}>
            <label htmlFor="discount-percent">Percentage</label>
            <input
              id="discount-percent"
              type="number"
              min="0.01"
              max="99"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="30"
            />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="discount-cap">Up to ₹ (optional)</label>
            <input
              id="discount-cap"
              type="number"
              min="0.01"
              step="0.01"
              value={cap}
              onChange={(e) => setCap(e.target.value)}
              placeholder="50"
            />
          </div>
        </div>
      ) : null}
      {kind === 'flat' || kind === 'fixed_price' ? (
        <div>
          <label htmlFor="discount-amount">
            {kind === 'flat' ? 'Amount off (₹)' : 'Price for the dish (₹)'}
          </label>
          <input
            id="discount-amount"
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
      ) : null}
      <div>
        <label htmlFor="discount-label">
          {kind === 'text'
            ? 'Words on the dish'
            : 'Words on the dish (optional; we write them if you do not)'}
        </label>
        <input
          id="discount-label"
          value={label}
          maxLength={40}
          onChange={(e) => setLabel(e.target.value)}
          placeholder={kind === 'text' ? 'Chef’s pick' : '60% OFF select items'}
        />
      </div>

      <fieldset>
        <legend className="muted">Applies to</legend>
        {kind === 'fixed_price' ? (
          <p className="muted">A fixed price is for one dish. Choose it below.</p>
        ) : (
          <>
            <label className="radio">
              <input
                type="radio"
                name="discount-cover"
                checked={cover === 'all'}
                onChange={() => setCover('all')}
              />
              Every dish
            </label>
            <label className="radio">
              <input
                type="radio"
                name="discount-cover"
                checked={cover === 'items'}
                onChange={() => setCover('items')}
              />
              Chosen dishes
            </label>
            <label className="radio">
              <input
                type="radio"
                name="discount-cover"
                checked={cover === 'category'}
                onChange={() => setCover('category')}
              />
              A category
            </label>
          </>
        )}
        {effectiveCover === 'items' ? (
          <ul className="discount-dishes">
            {dishes.length === 0 ? <li className="muted">No dishes yet.</li> : null}
            {dishes.map((d) => (
              <li key={d.id}>
                <label className="radio">
                  <input
                    type="checkbox"
                    checked={chosen.includes(d.id)}
                    onChange={() => toggle(d.id)}
                  />
                  {d.label}
                </label>
              </li>
            ))}
          </ul>
        ) : null}
        {effectiveCover === 'category' ? (
          <select
            aria-label="category"
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
          >
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        ) : null}
      </fieldset>

      <div className="row">
        <div style={{ flex: 1 }}>
          <label htmlFor="discount-from">Starts</label>
          <input
            id="discount-from"
            type="datetime-local"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            required
          />
        </div>
        <div style={{ flex: 1 }}>
          <label htmlFor="discount-to">Ends</label>
          <input
            id="discount-to"
            type="datetime-local"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            required
          />
        </div>
      </div>

      <div className="row">
        <button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Creating…' : 'Create offer'}
        </button>
      </div>
      {problem ? (
        <p role="alert" className="error">
          {problem}
        </p>
      ) : null}
      {create.error ? (
        <p role="alert" className="error">
          {create.error instanceof ApiError ? create.error.message : 'could not create the offer'}
        </p>
      ) : null}
    </form>
  );
}

/** What an offer says on the dish, for the list. */
export function describeDiscount(o: {
  readonly discountType: DiscountType;
  readonly value?: number | undefined;
  readonly capPaise?: number | undefined;
  readonly label?: string | undefined;
}): string {
  if (o.label) return o.label;
  const rupees = (p: number): string => (p % 100 === 0 ? String(p / 100) : (p / 100).toFixed(2));
  if (o.discountType === 'percent') {
    return `${Number(((o.value ?? 0) / 100).toFixed(2))}% OFF${o.capPaise ? ` up to ₹${rupees(o.capPaise)}` : ''}`;
  }
  if (o.discountType === 'flat') return `₹${rupees(o.value ?? 0)} OFF`;
  if (o.discountType === 'fixed_price') return `ONLY ₹${rupees(o.value ?? 0)}`;
  return '';
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}
function local(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function defaultFrom(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() + 5, 0, 0);
  return local(d);
}
function defaultTo(): string {
  const d = new Date();
  d.setHours(d.getHours() + 2, 0, 0, 0);
  return local(d);
}
function fromLocal(v: string): string | null {
  const t = Date.parse(v);
  return Number.isFinite(t) ? new Date(t).toISOString() : null;
}
